import {
  BitkitAlert,
  BitkitButton,
  BitkitLink,
  BitkitList,
  IconArrowNortheast,
  IconCheck,
  rem,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Image } from '@chakra-ui/react/image';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';

import { useBuildCacheAddonTrial } from '@/client/BFF/Organizations/AddonTrials/useBuildCacheAddonTrial';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import URLs from '@/enums/urls';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import ActivationAlert from '@/pages/BuildCache/components/BuildCacheActivation/ActivationAlert';
import buildCacheGetStartedImg from '@/pages/BuildCache/components/BuildCacheActivation/assets/build-cache-get-started.png';
import { BuildCacheActivationSourcePageType } from '@/pages/BuildCache/components/BuildCacheActivation/types/BuildCacheActivation';
import { buildCacheConnectionPath } from '@/utils/monolithApiRouteService';

const BENEFITS = [
  'Faster builds and tests, fewer CI minutes',
  'Support for Gradle, Bazel, Xcode, and React Native, on any CI',
  'Track cache hits and find your slowest tasks',
  'Fully managed by Bitrise, nothing to host',
] as const;

type ActivationCardProps = {
  sourcePageType: BuildCacheActivationSourcePageType;
  projectId?: string;
  blogPostHref?: string;
};

const ActivationCard = ({ sourcePageType, projectId, blogPostHref }: ActivationCardProps) => {
  const { isMobile } = useResponsive();

  const {
    account: {
      slug: accountSlug,
      hasBuildCacheSubscription: hasSubscription,
      buildCacheSubscriptionRequest: subscriptionRequest,
      canActivateBuildCacheTrial: canActivateTrial,
      canViewBillingData: isOwner,
    },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { trial, isLoading: isBuildCacheTrialLoading } = useBuildCacheAddonTrial(accountSlug);

  const isSubscriptionPending = !!subscriptionRequest?.requestedAt && !hasSubscription;
  const isTrialExpired = !!trial && !trial.isActive;
  const showActivationAlert = isSubscriptionPending || isTrialExpired;
  const canStartTrial =
    !isBuildCacheTrialLoading && !showActivationAlert && canActivateTrial && !trial && !hasSubscription;
  const showOnlyMembersAlert = !showActivationAlert && !canActivateTrial;

  return (
    <Card.Root
      asChild
      data-testid="activationCard"
      data-clarity-unmask="true"
      height="initial"
      width="100%"
      maxWidth={rem(985)}
      elevation={false}
    >
      <Card.Body
        display="flex"
        flexDir={['column', 'row']}
        justifyContent="space-between"
        gap="32"
        paddingInlineStart="32"
      >
        <Box display="flex" flexDir="column" gap="24" width="100%" maxWidth={['100%', rem(526)]}>
          <Box display="flex" flexDir="column" gap="24">
            <Box display="flex" flexDir="column" gap="4">
              <Text as="h2" textStyle="heading/h2" color="text/primary">
                Build faster by reusing what hasn’t changed
              </Text>
              <Text textStyle="body/lg/regular" color="text/body">
                Build Cache works across your build and test tasks, automatically.
              </Text>
            </Box>
            <BitkitList>
              {BENEFITS.map((benefit) => (
                <BitkitList.Item key={benefit} icon={IconCheck} iconColor="icon/positive">
                  {benefit}
                </BitkitList.Item>
              ))}
            </BitkitList>
          </Box>

          {canStartTrial && (
            <>
              <Text textStyle="body/lg/semibold" color="text/body">
                Set up in minutes, or let your AI agent do it with a single prompt.
              </Text>
              <Box display="flex" flexDir="column" alignItems="flex-start" gap="8">
                <BitkitButton
                  href={buildCacheConnectionPath(accountSlug)}
                  data-intercom-target="Set up Build Cache button"
                  onClick={() => {
                    segmentTrack('Add New Build Cache Connection Flow Started', {
                      ...(projectId ? { project_id: projectId } : {}),
                      source: sourcePageType,
                      workspace_slug: accountSlug,
                    });

                    segmentTrack('Build Cache Trial Start Attempted', {
                      source: sourcePageType,
                      has_potential_savings_data: false,
                      workspace_slug: accountSlug,
                    });
                  }}
                >
                  Start 30-day free trial
                </BitkitButton>
                <Text textStyle="body/sm/regular" color="text/secondary">
                  No credit card needed.
                  {isOwner && (
                    <>
                      {' '}
                      <BitkitLink href={URLs.PRICING_BUILD_CACHE} colorVariant="purple" isExternal>
                        See pricing.
                      </BitkitLink>
                    </>
                  )}
                </Text>
              </Box>
            </>
          )}

          {showActivationAlert && <ActivationAlert sourcePageType={sourcePageType} alignSelf="stretch" trial={trial} />}

          {showOnlyMembersAlert && (
            <BitkitAlert
              variant="info"
              data-testid="activationNotification"
              messageText="Only members of this workspace can start a Build Cache trial."
            />
          )}
        </Box>

        <Separator orientation={isMobile ? 'horizontal' : 'vertical'} height="auto" />

        <Box
          display="flex"
          flexDir="column"
          alignItems="center"
          justifyContent="space-between"
          gap="24"
          width="100%"
          maxWidth={['100%', rem(326)]}
        >
          <Image src={buildCacheGetStartedImg} alt="" width="100%" maxWidth={rem(272)} />
          {blogPostHref && (
            <Box display="flex" flexDir="column" gap="2" width="100%">
              <Text textStyle="heading/h6" color="text/secondary">
                From the blog
              </Text>
              <BitkitLink
                href={blogPostHref}
                isExternal
                colorVariant="purple"
                textStyle="body/md/regular"
                suffixIcon={IconArrowNortheast}
                suffixIconSize="16"
                onClick={() => segmentTrack('Read The Benchmark Study Link Clicked')}
              >
                See Build Cache results on real projects
              </BitkitLink>
            </Box>
          )}
        </Box>
      </Card.Body>
    </Card.Root>
  );
};

export default ActivationCard;
